import React, { useRef, useState, useMemo, useCallback } from "react";
import { message, Modal, Button, Form, Drawer } from "antd";
import { connect } from "dva";
import { useStates } from "@/utils/address";
import { deleteDealer, getDealers } from "@/services/nissanDealers";
import useScreenDetector from "@/hooks/useScreenDetector";
import DrawerWrapper from "@/components/DrawerWrapper";
import ListView from "@/components/ListView";
import { ListToBeSorted } from "@/components/SortAndFilter";
import NissanDealerForm from "@/pages/WWLNissan/NissanDealers/components/NissanDealerForm";
import useNissanDealersTableColumns from "./utils/useNissanDealersTableColumns";

const { confirm } = Modal;

const NissanDealers = ({ configPerm, configLang }) => {
  const actionRef = useRef();

  const { states, loading: loadingStates } = useStates();

  const [dealerForm] = Form.useForm();
  const { isMobile } = useScreenDetector();

  const [totalItems, setTotalItems] = useState(0);
  const [editDealer, setEditDealer] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(false);

  const fetchDealerList = useCallback(async ({ filters }) => {
    try {
      const response = await getDealers(filters);
      const newTotalItems = response?.totalCount ?? 0;
      setTotalItems(newTotalItems);

      return {
        data: response?.items ?? [],
        total: newTotalItems,
        success: true,
      };
    } catch (error) {
      return {
        data: [],
        total: 0,
      };
    }
  }, []);

  const handleEdit = useCallback(
    dealer => {
      setEditMode(true);

      const currentStateObj = states?.find(state => state.shortName === dealer.state);
      setEditDealer({
        ...dealer,
        emails: dealer?.emails?.split(";"),
        stateShortName: currentStateObj?.shortName,
        stateId: currentStateObj?.id,
        stateName: currentStateObj?.name,
      });
    },
    [states],
  );

  const handleDelete = useCallback(dealer => {
    confirm({
      title: "Delete Dealer",
      content: `Are you sure you want to delete this dealer?`,
      onOk: async () => {
        const hide = message.loading("Deleting...", 0);
        await deleteDealer(dealer.id)
          .then(() => {
            message.success("Successfully deleted dealer");
            actionRef.current.reload();
          })
          .catch(() => {
            message.error("Error deleting dealer");
          })
          .finally(() => {
            hide();
          });
      },
    });
  }, []);

  const onMenuItemClick = useCallback(
    (key, dealer) => {
      if (key === "delete") {
        handleDelete(dealer);
      } else if (key === "edit") {
        handleEdit(dealer);
      }
    },
    [handleDelete, handleEdit],
  );

  const columns = useNissanDealersTableColumns({ onMenuItemClick });

  const onClose = useCallback(() => {
    setEditDealer(null);
    dealerForm?.resetFields();
  }, [dealerForm]);

  const toolBarRender = useMemo(
    () =>
      function render() {
        const buttonList = [];

        if (configPerm["NissanService.Dealers.Create"]) {
          buttonList.push(
            <Button
              type="primary"
              onClick={() => {
                setEditMode(false);
                setEditDealer({});
              }}
            >
              {configLang.Autosled.New}
            </Button>,
          );
        }

        return buttonList;
      },
    [configPerm, configLang],
  );

  return (
    <ListView
      ref={actionRef}
      filterType={ListToBeSorted.NissanDealers}
      columns={columns}
      sortingSeparator=" "
      request={fetchDealerList}
      toolBarRender={toolBarRender}
      totalItems={totalItems}
    >
      <DrawerWrapper visible={!!editDealer}>
        <Drawer
          title={editMode ? "Edit Dealer" : "Create Dealer"}
          width={isMobile ? "80%" : "40%"}
          onClose={onClose}
          open={!!editDealer}
          bodyStyle={{ paddingBottom: 80 }}
          footer={
            <div style={{ textAlign: "right" }}>
              <Button onClick={onClose} style={{ marginRight: 8 }}>
                Cancel
              </Button>
              <Button type="primary" onClick={dealerForm.submit} loading={loading}>
                Submit
              </Button>
            </div>
          }
        >
          {!!editDealer && (
            <NissanDealerForm
              formRef={dealerForm}
              initialValues={editDealer}
              refreshTable={actionRef?.current?.reload}
              setLoading={setLoading}
              setVisible={() => setEditDealer(null)}
              editMode={editMode}
              states={states}
              loadingStates={loadingStates}
            />
          )}
        </Drawer>
      </DrawerWrapper>
    </ListView>
  );
};

export default connect(({ configs }) => ({
  configPerm: configs.configPerm,
  configLang: configs.configLang,
}))(NissanDealers);
